import { LabelBold } from '@comeon/ui';
import styled, { css } from 'styled-components';
import Icon, { ICON_CHEVRON_UP, ICON_RIGHT } from '../../../../../icon/index';
import SportsbookSportIcon from '../../sportsbook-sport-icon';
import { Link } from '~/utils/navigation';

type IconChevronUpProps = {
	isExpanded?: boolean;
};

const IconChevronUp = styled(Icon).attrs({
	type: ICON_CHEVRON_UP
})<IconChevronUpProps>`
	transform: rotate(180deg);

	${({ isExpanded }) =>
		isExpanded &&
		css`
			transform: rotate(0deg);
		`}
`;

const SportIconWrapper = styled.span`
	flex: 0 0 auto;
	width: 24px;
	height: 24px;
`;

const StyledSportIcon = styled(SportsbookSportIcon)`
	height: 24px;
	width: 24px;
`;

type WrapperProps = {
	isExpanded?: boolean;
};

const Wrapper = styled.div<WrapperProps>`
	width: 100%;
	// Due to design + 1px to solve .5px issue https://www.figma.com/file/OqG3QvTMAt7a8jO6BBscTl/%E2%9A%BD-Sportsbook-PWD---Design-System?node-id=4022%3A35786&mode=dev
	min-height: 45px;
	display: flex;
	justify-content: space-between;
	gap: 8px;
	padding: 12px;
	border-radius: var(--base-radius-large);
	background-color: var(--main-surface-1);
	background-image: linear-gradient(to right, var(--background-to-surface-gradient), var(--main-surface-1));
	border: 1px solid var(--main-background);

	${({ isExpanded }) =>
		isExpanded &&
		css`
			border-bottom: 1px solid var(--main-background);
			border-radius: var(--base-radius-large) var(--base-radius-large) 0 0;
			margin-bottom: 0;
			border-top: none;
			border-right: none;
			border-left: none;
		`}

	${IconChevronUp}, ${StyledSportIcon} {
		color: var(--main-icons);
	}
`;

const Title = styled(LabelBold)`
	display: -webkit-box;
	-webkit-line-clamp: 2;
	-webkit-box-orient: vertical;
	overflow: hidden;
	color: var(--main-text);
`;

const IconRight = styled(Icon)`
	flex: 0 0 auto;
	align-self: flex-start;
	color: var(--main-action-icons);
`;
const TitleWrapper = styled.div<{ hasHoverEffect?: boolean }>`
	display: flex;
	align-items: center;
	gap: 6px;

	${({ hasHoverEffect }) =>
		hasHoverEffect &&
		css`
			@media (hover: hover) and (pointer: fine) {
				&:hover {
					${StyledSportIcon}, ${Title}, ${IconRight} {
						color: var(--main-text-primary);
					}
				}
			}
		`}
`;

const ExpandableClickArea = styled.div<{
	hasPointer?: boolean;
}>`
	display: flex;
	justify-content: flex-end;
	flex: 1;

	${({ hasPointer }) =>
		hasPointer &&
		css`
			cursor: pointer;
		`}
`;

type GameCardListHeaderProps = {
	title?: string;
	titleLink?: string;
	sportIcon?: string;
	isExpanded?: boolean;
	onExpandToggle?: () => void;
	className?: string;
};

/**
 * Header for list of game cards.
 * Use it together with GameCardListHeader and GameCardListFooter.
 *
 * @param title Title of the list
 * @param sportIcon Icon for the sport
 * @param isExpanded If true, header will act as expanded
 * @param onExpandToggle Callback for expand toggle event
 * @param className Optional styling override
 */
const GameCardListHeader: React.FC<GameCardListHeaderProps> = ({
	title,
	titleLink,
	sportIcon,
	isExpanded = true,
	onExpandToggle,
	className
}) => {
	const isExpandable = !!onExpandToggle;
	const hasLink = !!titleLink;

	return (
		<Wrapper isExpanded={isExpanded} className={className}>
			{hasLink ? (
				<Link href={titleLink}>
					<TitleWrapper hasHoverEffect>
						{sportIcon && (
							<SportIconWrapper>
								<StyledSportIcon icon={sportIcon} />
							</SportIconWrapper>
						)}
						<Title>{title || ''}</Title>
						<IconRight type={ICON_RIGHT} />
					</TitleWrapper>
				</Link>
			) : (
				<TitleWrapper>
					{sportIcon && (
						<SportIconWrapper>
							<StyledSportIcon icon={sportIcon} />
						</SportIconWrapper>
					)}
					<Title>{title || ''}</Title>
				</TitleWrapper>
			)}
			{isExpandable && (
				<ExpandableClickArea hasPointer={isExpandable} onClick={onExpandToggle}>
					<IconChevronUp type={ICON_CHEVRON_UP} isExpanded={isExpanded} />
				</ExpandableClickArea>
			)}
		</Wrapper>
	);
};

export { Wrapper, TitleWrapper, Title };

export default GameCardListHeader;
